import { Check, EditCalendarOutlined, ElectricMeter, KeyboardDoubleArrowDown, KeyboardDoubleArrowUp, Warning } from '@mui/icons-material';
import { Alert, AlertColor, Box, Card, CardActions, CardContent, CardHeader, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import moment from 'moment';
import route from '../../Router';
import NavLink, { NavButton } from '../../components/NavLink';
import AccentPaper from '../../components/Paper';
import { AnalyticFilterProps, CircularProgressItem, ComponentItem, styles as afStyles } from '../../components/data/AnalyticFilter';
import DataTable, { ColumnData, DataListComponent, NoDataPaper } from '../../components/data/DataTable';
import DataView from '../../components/data/DataView';
import { Calendar, Graph } from '../../icons';
import { CachedModel } from '../../models/CachedStore';
import { useCachedModel, useCachedStore } from '../../models/Hooks';
import { Measure } from '../../models/Types';
import { GroupByItem, calendarWithouTime, groupBy, isMobile } from '../../utils';
import HouseCombo, { houseStore } from '../houses/HouseCombo';
import { tariffStore } from '../project/tariff/TariffCombo';
import { MeasureForm, measureParse } from './MeasureForm';
import MeterTariffCombo, { meterTariffStore } from './MeterTariffCombo';

export const lastMeasureDay = 25;

const analyticFilter: AnalyticFilterProps = {
    url: "MeasureAnalytic",
    items: data => {
        const meters = data.Meters as any;
        var percent = (1 - meters.Overdue / meters.All) * 100;
        const color = percent < 50 ? 'error' : percent == 100 ? 'success' : 'warning';

        const result = [
            <CircularProgressItem data={data} item={{
                key: 'Meters',
                icon: <ElectricMeter color={color} />,
                color
            }} funcs={{
                percent: () => percent,
                sub: () => <>Не подано: {meters.Overdue}</>,
                value: () => <>Всего: {meters.All}</>,
                title: () => <>Счетчики</>
            }} />
        ];

        (data?.Measures as any[])?.map(x => <ComponentItem
            component={(cur, props) => <Box sx={afStyles.componentWrapper}>
                {props.data.Cur > props.data.Prev ?
                    <KeyboardDoubleArrowUp fontSize="large" color="error" /> :
                    props.data.Cur < props.data.Prev ?
                        <KeyboardDoubleArrowDown fontSize="large" color="success" /> :
                        null}
            </Box>}
            data={x}
            item={{
                key: 'MeterTariffId',
                icon: <ElectricMeter color={color} />,
                color
            }}
            funcs={{
                title: (c, i, d) => meterTariffStore.data?.find(x => x.Id === d.MeterTariffId)?.Value,
                value: (c, i, d) => 'Предыдущее: ' + d.Prev,
                sub: (c, i, d) => <NavLink route={[['hideForm'], ['id', 'new'], ['MeterTariffId', d.MeterTariffId]]} fontWeight="bold" color={d.Cur ? undefined : 'error'}>
                    {d.Cur || 'Подать показания'}
                </NavLink>,
            }} />).forEach(x => result.push(x));

        return result;
    }
};

const styles = {
    mobile: {
        dataView: {
            flexDirection: 'column',
            "&>.datatable-compact>.MuiToolbar-root, & .datatable-toolbar": {
                display: 'none'
            }
        },
        list: {
            "& .viewmodes-selector": {
                display: 'none'
            }
        },
        papers: {
            gap: 2,
            p: 2,
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'space-between'
        }
    }
};

const columns: ColumnData<Measure>[] = [{
    label: 'Ресурс',
    name: 'MeterTariffId',
    routerLink: true,
    store: meterTariffStore,
    filter: (onChange) => <MeterTariffCombo onChange={e => onChange(e.target.value)} />,
    renderer: (val, obj) => meterTariffStore.data?.find(x => x.Id == val)?.Value
}, {
    label: 'Дата снятия',
    name: 'ReadDate',
    filter: true,
    type: 'date',
}, {
    label: 'Дата передачи',
    name: 'CreateDate',
    filter: true,
    type: 'date',
}, {
    label: 'Дом',
    name: 'HouseId',
    store: houseStore,
    filter: (onChange) => <HouseCombo onChange={e => onChange(e.target.value)} />,
    renderer: (val, obj) => houseStore.data?.find(x => x.Id == val)?.Value
}, {
    label: 'Показание',
    name: 'Value',
    type: 'numeric',
    filter: true
}];

interface MeasureAnalytic {
    Measures: {
        Cur: number
        Prev: number
        CurDate: Date
        PrevDate: Date
        MeterTariffId: number
    }[]
    Meters: {
        All: number
        Overdue: number
    }
}

const stateColor: AlertColor[] = ['info', 'success', 'warning', 'error'];
const stateText = [
    null,
    'Все показания переданы',
    `Передача показаний осуществляется до ${lastMeasureDay} числа текущего месяца`,
    'Последний день внесения показаний'
];

export function getBillingState(noOverdue?: boolean) {
    const day = (new Date()).getDate();
    return noOverdue ? 1 : (day < lastMeasureDay ? 2 : (day === lastMeasureDay ? 3 : 0));
}

export function getBillingStateText(noOverdue?: boolean) {
    return stateText[getBillingState(noOverdue)]
}

export function getBillingStateColor(noOverdue?: boolean) {
    return stateColor[getBillingState(noOverdue)]
}

function noData() {
    return <NoDataPaper title="Отсутствуют счетчики" content={[
        'В этом разделе можно будет передавать показания счетчиков.',
        <>Для уточнения информации о счетчиках вы можете обратиться в УК с помощью раздела <NavLink route={[['page', 'request'], ['id', 'new']]}>Заявки</NavLink></>
    ]} />;
}


const measureAnalyticStore = CachedModel.ConfigureModel<MeasureAnalytic>('MeasureAnalytic', 'MeasureAnalytic', 10 * 60);
function MobileView({ onList }: { onList: () => any }) {
    const [data] = useCachedModel(measureAnalyticStore, undefined, true);
    const [meters] = useCachedStore(meterTariffStore);
    const [tariffs] = useCachedStore(tariffStore);

    if (!data?.Meters?.All) {
        return noData();
    }

    const state = getBillingState(!data.Meters.Overdue);

    return <>
        <Box sx={{ p: 1 }}>
            {state ?
                <Alert icon={state === 1 ? <Check /> : state === 2 ? <EditCalendarOutlined /> : <Warning />} severity={stateColor[state]}>{stateText[state]}</Alert> :
                null}

            <Card elevation={2} sx={{ mt: 2.5 }}>
                <CardHeader title={`Последние показания${data.Measures?.length ? '' : ' отсутствуют'}`} />
                <CardContent component={Stack} sx={{ gap: 1.25 }}>
                    {data.Measures.map(m => {
                        const meter = meters?.find(x => x.Id === m.MeterTariffId);
                        const tariff = tariffs?.find(x => x.Id === meter?.Obj?.TariffId);
                        const cd = new Date(m.CurDate);
                        return <Typography variant="body2">
                            <Typography color="textSecondary" variant="inherit" component="span">{meter?.Value}, {tariff?.Obj?.Unit}:</Typography> {m.Cur || '-'}
                            {cd?.getFullYear() > 2000 ? ' (' + moment(cd).calendar() + ')' : null}
                        </Typography>
                    })}
                </CardContent>
                <CardActions>
                    {getBillingState() ? 
                        <NavButton variant="contained" size="large" sx={{ flex: 1 }} route={[['id', 'new'], ['hideForm']]}>Отправить показания</NavButton> :
                        <Alert icon={<EditCalendarOutlined />} severity={stateColor[2]}>{stateText[2]}</Alert>}
                </CardActions>
            </Card>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 3 }}>
                <AccentPaper sx={styles.mobile.papers} onClick={onList}>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", "&>svg": { width: 50 } }}>
                        <Box>
                            <Typography variant="h2">История показаний</Typography>
                            <Typography component="div" variant="caption" color="textSecondary">все ваши показания приборов учета</Typography>
                        </Box>
                        {Calendar}
                    </Box>
                    {/*<Button variant="contained">Открыть</Button>*/}
                </AccentPaper>

                <Paper elevation={2} sx={styles.mobile.papers}>
                    <Box display="flex" gap={1} sx={{ "&>svg": { width: 50 } }} >
                        <Box>
                            <Typography variant="h2">График потребления</Typography>
                            <Typography component="div" variant="caption" color="textSecondary">электроэнергиии и воды за выбранный период</Typography>
                        </Box>
                        {Graph}
                    </Box>
                    {/*<Button variant="contained">Смотреть</Button>*/}
                </Paper>
            </Box>
        </Box>
    </>;
}

class MeasureList extends DataListComponent<Measure> {
    renderNoData() {
        return noData();
    }

    getData() {
        return groupBy(super.getData(), x => new Date(x.CreateDate.getFullYear(), x.CreateDate.getMonth(), x.CreateDate.getDate()).getTime())
            ?.sort((a, b) => a.key > b.key ? -1 : 1) as any[];
    }

    renderItem(row: Measure, i: number) {
        return <ListItem><Paper elevation={2}>{this.buildCols(row, i)}</Paper></ListItem>;
    }

    buildCols(_row: Measure, i: number) {
        const row = _row as any as GroupByItem<Measure>;
        return [
            <ListItemText primary={calendarWithouTime(row.key)} />,
            ...row.map(x => {
                let mt = meterTariffStore.data?.find(m => m.Id == x.MeterTariffId);
                return <ListItemText
                    secondary={mt?.Value + ', ' + tariffStore.data?.find(m => m.Id == mt?.Obj?.TariffId)?.Obj?.Unit + ' :'}
                    primary={x.Value}
                    sx={{ flexDirection: 'row-reverse', justifyContent: 'flex-end!important', gap: 1 }} />
            })
        ];
    }
}

export default function MeasureView() {
    const ism = isMobile();

    var vm = route.get('view') || 'card';
    if (vm !== 'card' && vm !== 'list') {
        vm = 'card';
        route.setState([['view']]);
    }

    return <DataView<Measure> key={vm}
        analyticFilter={ism || true ? undefined : analyticFilter}
        title="Показание"
        titleMultiple="Показания"
        typeId="Measure"
        viewSx={ism ? vm === 'card' ? styles.mobile.dataView : styles.mobile.list : undefined}
        viewMode={ism ? vm : undefined}
        modes={ism ? [{
            mode: 'list',
        }, {
            mode: 'card',
            hideAddBtn: true,
            view: () => <MobileView onList={() => route.setState([['view', 'list']])} />
        }] : undefined}
        form={(id, apiRef) => <MeasureForm key="main" id={id} apiRef={apiRef} />}
        tableRenderer={p => ism ? <MeasureList {...p} /> : <DataTable<Measure> {...p} />}
        tableProps={{
            columns,
            noDataRenderer: () => <NoDataPaper title="Показания отсутствуют" content={[
                'В этом разделе отображаются переданные показания счетчиков.',
                <>Для уточнения информации о счетчиках вы можете обратиться в УК с помощью раздела <NavLink route={[['page', 'request'], ['id', 'new']]}>Заявки</NavLink></>
            ]} />,
            dataParser: measureParse,
        }} />;
}